import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Centralized SEO component for Spirit & Associates pages.
 * Renders <title>, meta description, canonical, Open Graph, Twitter tags,
 * and optional JSON-LD structured data.
 */
export default function SEO({
  title,
  fullTitleOverride,      // If set, used verbatim as <title> (no suffix appended)
  description,
  path = "",
  image,
  type = "website",
  siteName = "Spirit & Associates",
  siteUrl = "https://spiritandassociates.org",
  keywords,
  noIndex = false,
  publishedTime,
  modifiedTime,
  jsonLd,                 // Optional object or array — rendered as <script type="application/ld+json">
}) {
  const fullUrl = `${siteUrl}${path || ""}`.replace(/\/$/, path === "" || path === "/" ? "/" : "");
  const fullTitle = fullTitleOverride
    ? fullTitleOverride
    : title
    ? `${title} — ${siteName}`
    : `${siteName} — Special People Inspiring Recovery In Time`;
  const desc =
    description ||
    "Spirit & Associates provides compassionate interventions, recovery coaching, 5329 firearms evaluations, and virtual sessions in Pennsylvania.";
  const ogImage = image || `${siteUrl}/favicon-512.png`;
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={fullUrl} />
      {noIndex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}
      {modifiedTime ? <meta property="article:modified_time" content={modifiedTime} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data */}
      {jsonLdStr ? (
        <script type="application/ld+json">{jsonLdStr}</script>
      ) : null}
    </Helmet>
  );
}
